class CartDrawerSuggestions extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController()

    // NB: must not be named `this.slot` — Element.slot is a reserved property that
    // reflects the slot attribute as a string, so assigning an element silently
    // coerces to "[object HTMLDivElement]" and replaceChildren() throws on commit.
    this._slot = this.querySelector('[data-recommendations-slot]')

    this.recommendationsUrl = this.dataset.recommendationsUrl
    this.sectionId = this.dataset.sectionId
    this.intent = this.dataset.intent || 'complementary'
    this.limit = parseInt(this.dataset.limit, 10) || 4
    this.purchasedIds = this._parseIdSet(this.dataset.purchasedIds)
    this._fallbackUrl = this.dataset.fallbackUrl || ''
    this._activeIntent = null
    this._groupTitle = null

    this._headingEl = this.querySelector('.cart-drawer__suggestions-heading')
    this._headingComplementary = this._headingEl ? this._headingEl.textContent : ''
    this._headingRelated = this.dataset.headingRelated || this._headingComplementary
    // Per-product template, e.g. "Add-ons for your [product]" — [product] is replaced
    // with the cart line's title for the single-group heading and each group's
    // sub-heading. Falls back to the umbrella heading if unset.
    this._headingProduct = this.dataset.headingProduct || ''

    // RPM Fitment integration lives in a sibling module (cart-suggestions-fitment.js,
    // loaded first). If it's absent the recs simply run without fit filtering/badges.
    this._fitment = typeof CartSuggestionsFitment === 'function' ? new CartSuggestionsFitment() : null

    this._seedCap = 5
    // Cap distinct per-product add-on groups shown at once. Curated complements are
    // high-relevance, but the drawer must stay compact and keep the cart items and
    // checkout CTA prominent (Baymard), so show groups for the N most-recently-added
    // products and no more; the rest stay reachable from those products' PDPs.
    this._maxGroups = 2
    this._latestCart = null
    this._fetchToken = 0
    this._cartFetchToken = 0
    this._fetchAbort = null

    this._bindEvents()
  }

  _bindEvents() {
    const opts = { signal: this.abortController.signal }
    document.addEventListener('drawerOpen.CartDrawer', this._onDrawerOpen.bind(this), opts)
    document.addEventListener('cart:updated', this._onCartUpdate.bind(this), opts)
    document.addEventListener('ajaxProduct:added', this._onProductAdded.bind(this), opts)

    // Re-filter when the customer's selected machine / garage changes.
    const onMachine = this._onMachineChange.bind(this)
    document.addEventListener('rpm:machine:selected', onMachine, opts)
    document.addEventListener('rpm:machine:cleared', onMachine, opts)
    document.addEventListener('rpm:garage:loaded', onMachine, opts)
    document.addEventListener('rpm:garage:updated', onMachine, opts)
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort()
    if (this._fetchAbort) this._fetchAbort.abort()
    clearTimeout(this._machineDebounce)
  }

  // Defer the first cart fetch until the drawer actually opens — avoids an
  // unconditional /cart.js request on every pageview when most visitors never
  // engage the cart drawer. cart:updated only dispatches from quantityChanged
  // (theme.js:1861), never from initial buildCart, so we cannot rely on it
  // for first-paint hydration.
  _onDrawerOpen() {
    if (this._latestCart) return
    this._refreshCartAndLoad()
  }

  _onCartUpdate(event) {
    const cart = event && event.detail && event.detail.cart
    if (!cart) return
    this._latestCart = cart
    this._loadFromCart(cart)
  }

  _onProductAdded(event) {
    const product = event && event.detail && event.detail.product
    if (!product || !product.product_id) return
    // The added product is now a cart line item; re-seed from the full cart.
    this._refreshCartAndLoad()
  }

  _refreshCartAndLoad() {
    const token = ++this._cartFetchToken
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(cart => {
        if (!this.isConnected || token !== this._cartFetchToken) return
        this._latestCart = cart
        this._loadFromCart(cart)
      })
      .catch(() => {})
  }

  _loadFromCart(cart) {
    const seeds = this._cartSeeds(cart)
    if (seeds.length === 0) {
      this._teardown()
      return
    }
    // Show the skeleton only on the first load (nothing rendered yet). On a
    // re-load with cards already visible (add-to-cart, machine change) keep the
    // current cards until the new set commits — no flash of skeleton over content.
    if (this.hasAttribute('hidden')) this._showSkeleton()
    this._loadForSeeds(seeds)
  }

  // Unique cart products, most-recently-added first, capped to bound the number of
  // parallel recommendation requests. Each seed carries the product title (to head a
  // complementary group "Add-ons for your <title>") and handle (to fetch that product's
  // complements in its own context — see _buildUrl).
  _cartSeeds(cart) {
    if (!cart || !Array.isArray(cart.items)) return []
    const seen = new Set()
    const seeds = []
    for (let i = cart.items.length - 1; i >= 0; i--) {
      const item = cart.items[i]
      const id = item && item.product_id
      if (!id || seen.has(id)) continue
      seen.add(id)
      seeds.push({ id: id, title: item.product_title || item.title || '', handle: item.handle || '' })
      if (seeds.length >= this._seedCap) break
    }
    return seeds
  }

  // Recommend across the WHOLE cart: fetch recs for every seed in parallel, merge
  // + dedupe, and surface products recommended by more cart items first.
  // Complementary (curated) is tried first; related is the behavioral fallback.
  _loadForSeeds(seeds) {
    if (this._fetchAbort) this._fetchAbort.abort()
    this._fetchAbort = new AbortController()
    const token = ++this._fetchToken
    const intents = this.intent === 'related' ? ['related'] : [this.intent, 'related']
    this._tryIntent(seeds, intents, 0, token)
  }

  _tryIntent(seeds, intents, index, token) {
    if (token !== this._fetchToken) return
    if (index >= intents.length) {
      this._tryFallback(token)
      return
    }
    const intent = intents[index]
    const advance = () => this._tryIntent(seeds, intents, index + 1, token)
    this._fetchAllSeeds(seeds, intent, this._fetchAbort.signal)
      .then(results => {
        if (token !== this._fetchToken) return
        // Curated complements are grouped per cart product ("Add-ons for your X");
        // related/fallback are behavioral and merge into one cross-cart grid.
        if (intent === 'complementary') this._renderComplementary(results, advance)
        else this._renderMerged(results, intent, advance)
      })
      .catch(() => {
        if (token === this._fetchToken) advance()
      })
  }

  _renderMerged(results, intent, advance) {
    const merged = this._mergeAndRank(results.map(r => r.text))
    if (!merged || merged.children.length === 0) { advance(); return }
    const filtered = this._buildFilteredGrid(merged.outerHTML, intent)
    if (filtered && filtered.children.length > 0) {
      this._activeIntent = intent
      this._commitGrid(filtered)
    } else {
      advance()
    }
  }

  // Build one group per cart product from its complementary response, in
  // most-recently-added order. Each group is filtered (ownership dedup only — type
  // and fitment are bypassed for curated complements) then deduped against products
  // already shown in an earlier group, so a complement shared by two cart items
  // appears once. Empty groups are dropped; groups are capped to keep the drawer
  // compact. If nothing survives, the caller advances to the related fallback.
  _renderComplementary(results, advance) {
    if (!this.isConnected || !this._slot) return
    const groups = this._buildGroups(results)
    if (groups.length === 0) { advance(); return }
    this._activeIntent = 'complementary'
    this._commitGroups(groups)
  }

  _buildGroups(results) {
    const seen = new Set()
    const groups = []
    for (let i = 0; i < results.length && groups.length < this._maxGroups; i++) {
      const grid = this._parseGrid(results[i].text)
      if (!grid) continue
      this._filterCards(grid, 'complementary')
      this._dropSeen(grid, seen)
      this._sliceCards(grid, this.limit)
      if (grid.children.length === 0) continue
      Array.from(grid.children).forEach(card => seen.add(card.getAttribute('data-product-id')))
      groups.push({ title: results[i].seed.title, grid: grid })
    }
    return groups
  }

  _dropSeen(grid, seen) {
    Array.from(grid.children).forEach(card => {
      if (seen.has(card.getAttribute('data-product-id'))) card.remove()
    })
  }

  // When neither complementary nor related yields a fitting card, fall back to the
  // merchant's curated fallback collection — fetched on demand (only in this edge
  // case, so no per-page weight) and run through the same filters, so it surfaces
  // machine-fitting popular products, or hides if none fit / none configured.
  _tryFallback(token) {
    if (token !== this._fetchToken || !this._fallbackUrl) { this._stayHidden(); return }
    fetch(this._fallbackUrl, { signal: this._fetchAbort.signal })
      .then(r => r.text())
      .then(text => {
        if (token !== this._fetchToken) return
        const filtered = this._buildFilteredGrid(text, 'fallback')
        if (filtered && filtered.children.length > 0) {
          this._activeIntent = 'fallback'
          this._commitGrid(filtered)
        } else {
          this._stayHidden()
        }
      })
      .catch(() => { if (token === this._fetchToken) this._stayHidden() })
  }

  // One request per seed, in parallel. A failed seed resolves to '' so a single
  // bad response can't reject the whole batch.
  _fetchAllSeeds(seeds, intent, signal) {
    return Promise.all(seeds.map(seed => {
      const url = this._buildUrl(seed, intent)
      if (!url) return Promise.resolve({ seed: seed, text: '' })
      return fetch(url, { signal })
        .then(r => r.text())
        .then(text => ({ seed: seed, text: text }))
        .catch(() => ({ seed: seed, text: '' }))
    }))
  }

  // Merge per-seed responses into one grid: dedupe by product, then order by how
  // many seeds recommended each product (cross-cart relevance — a part that
  // complements several cart items ranks first), preserving seed-recency order
  // within ties (Map keeps insertion order; Array.sort is stable).
  _mergeAndRank(texts) {
    const seen = new Map()
    texts.forEach(text => {
      const grid = this._parseGrid(text)
      if (!grid) return
      Array.from(grid.children).forEach(card => {
        const id = card.getAttribute('data-product-id')
        if (!id) return
        if (seen.has(id)) seen.get(id).freq += 1
        else seen.set(id, { card: card, freq: 1 })
      })
    })
    if (seen.size === 0) return null
    const ranked = Array.from(seen.values()).sort((a, b) => b.freq - a.freq)
    const grid = document.createElement('div')
    grid.className = 'product-recommendations cart-drawer__suggestions-grid'
    ranked.forEach(entry => grid.appendChild(entry.card))
    return grid
  }

  // Complementary recs render in the PARENT product's own context via the Section Rendering
  // API (/products/{handle}?section_id=cart-complementary) so the section can fitment-match
  // the variant server-side — the Recommendations API never receives the source product, so
  // it can't. Related recs are behavioral (not product-specific) and keep the Recommendations
  // API; over-fetch (Shopify max=10) so client-side dedup still leaves enough to hit the limit.
  _buildUrl(seed, intent) {
    if (intent === 'complementary') {
      if (!seed.handle) return ''
      return '/products/' + encodeURIComponent(seed.handle) + '?section_id=cart-complementary'
    }
    return this.recommendationsUrl
      + '?section_id=' + encodeURIComponent(this.sectionId)
      + '&product_id=' + encodeURIComponent(seed.id)
      + '&limit=10'
      + '&intent=' + encodeURIComponent(intent)
  }

  _buildFilteredGrid(text, intent) {
    if (!this.isConnected || !this._slot) return null
    const grid = this._parseGrid(text)
    if (!grid) return null
    this._filterCards(grid, intent)
    this._sliceCards(grid, this.limit)
    return grid
  }

  _commitGrid(grid) {
    this._groupTitle = null
    this._slot.replaceChildren(grid)
    this._finishCommit()
  }

  // Commit grouped complementary sections. A single group needs no sub-heading or
  // wrapper — its product name becomes the umbrella heading, so the DOM matches the
  // ungrouped grid exactly (no extra layout). Multiple groups each get a <section>
  // with an "Add-ons for your X" sub-heading beneath the umbrella heading.
  _commitGroups(groups) {
    const single = groups.length === 1
    this._groupTitle = single ? groups[0].title : null
    const fragment = document.createDocumentFragment()
    groups.forEach(group => {
      fragment.appendChild(single ? group.grid : this._groupSection(group))
    })
    this._slot.replaceChildren(fragment)
    this._finishCommit()
  }

  _groupSection(group) {
    const section = document.createElement('section')
    section.className = 'cart-drawer__suggestions-group'
    const heading = document.createElement('h3')
    heading.className = 'cart-drawer__suggestions-group-heading'
    heading.textContent = this._productHeading(group.title)
    section.appendChild(heading)
    section.appendChild(group.grid)
    return section
  }

  _finishCommit() {
    this._applyHeading()
    this.removeAttribute('hidden')
    this.removeAttribute('aria-busy')
    this._initQuickAdds()
    // Behavioral recs get fit-suppression + "no-fit" badges + garage variant-defaulting here.
    // Curated complements bypass all of it: they're fetched in the parent product's context
    // (sections/cart-complementary.liquid) and already arrive with the fitment-matched variant
    // pre-selected server-side — parity with the PDP — so the JS leaves their variant alone.
    if (this._fitment && this._activeIntent !== 'complementary') {
      this._fitment.applyToCards(this._slot, (card, opt) => this._setCardPrice(card, opt))
    }

    document.dispatchEvent(new CustomEvent('recommendations:loaded', {
      detail: { section: this, intent: this._activeIntent || this.intent }
    }))
  }

  _applyHeading() {
    if (!this._headingEl) return
    this._headingEl.textContent = this._headingText()
    this._setHeadingSkeleton(false)
  }

  // - related/fallback fired when complementary was the configured intent: the
  //   "Pairs well…" promise no longer holds, so use the softer behavioral heading.
  // - a single complementary group: name the product directly ("Add-ons for your X").
  // - multiple complementary groups: the umbrella heading sits above the per-group
  //   "Add-ons for your X" sub-headings.
  _headingText() {
    const behavioral = this._activeIntent === 'related' || this._activeIntent === 'fallback'
    if (this.intent !== 'related' && behavioral) return this._headingRelated
    if (this._groupTitle) return this._productHeading(this._groupTitle)
    return this._headingComplementary
  }

  _productHeading(title) {
    if (!this._headingProduct || !title) return this._headingComplementary
    return this._headingProduct.replace('[product]', title)
  }

  // The heading copy is only known once the engine responds (see _applyHeading),
  // so during the skeleton it shows a neutral shimmer bar rather than an
  // optimistic label that would visibly flip on commit.
  _setHeadingSkeleton(on) {
    if (this._headingEl) {
      this._headingEl.classList.toggle('cart-drawer__suggestions-heading--skeleton', on)
    }
  }

  // Single-variant cards submit a real form to /cart/add.js; reuse the theme's
  // AjaxProduct handler so the add dispatches ajaxProduct:added (theme.js:2847)
  // — the event that CartDrawer (theme.js:4674) and the free shipping bar both
  // listen to for refresh/open. replaceChildren installs fresh form nodes on
  // every rebuild, so there is no risk of double-binding the same form.
  _initQuickAdds() {
    const hasAjax = !!(window.theme && typeof theme.AjaxProduct === 'function')
    this._slot.querySelectorAll('form[data-cart-quick-add]').forEach(form => {
      // These cards live inside the drawer's own <form id="CartDrawerForm">. A
      // quick-add submit must NEVER reach that form's submit handler (theme.js:1709
      // — checkout / T&C). The guard is unconditional and decoupled from AjaxProduct
      // detection: always stop the bubble, and if the AJAX add is unavailable also
      // cancel the native submit so the button is inert rather than submitting the
      // cart form.
      form.addEventListener('submit', event => {
        event.stopPropagation()
        if (!hasAjax) event.preventDefault()
      })
      if (!hasAjax) return
      new theme.AjaxProduct(form, '.add-to-cart')
      this._initVariantPrice(form)
    })
  }

  // Multi-variant cards add via an in-drawer <select name="id"> (the shopper picks a
  // variant without leaving the cart). Keep the displayed price in sync with the
  // selected option's data-price/data-compare (money pre-formatted server-side).
  _initVariantPrice(form) {
    const select = form.querySelector('select[name="id"]')
    if (!select) return
    const card = form.closest('.cart-suggestion-card')
    if (!card) return
    select.addEventListener('change', () => {
      this._setCardPrice(card, select.options[select.selectedIndex])
    })
  }

  _setCardPrice(card, opt) {
    const priceEl = card.querySelector('.cart-suggestion-card__price')
    const price = opt && opt.getAttribute('data-price')
    if (!priceEl || !price) return
    const compare = opt.getAttribute('data-compare')
    priceEl.innerHTML = compare
      ? '<span class="cart-suggestion-card__price-sale">' + price + '</span> '
        + '<s class="cart-suggestion-card__price-was">' + compare + '</s>'
      : price
  }

  // Re-run the full chain (complementary -> related -> fallback collection) when
  // the customer's machine / garage changes, so fit is re-evaluated against the
  // cart's recs rather than a stale filtered set.
  _onMachineChange() {
    clearTimeout(this._machineDebounce)
    this._machineDebounce = setTimeout(() => {
      if (this._latestCart) this._loadFromCart(this._latestCart)
    }, 150)
  }

  // DOMParser avoids two failure modes of innerHTML on a detached div:
  // older-browser script execution from <script> tags in the response, and
  // accumulation of duplicate <style> nodes across multiple refreshes.
  _parseGrid(text) {
    const doc = new DOMParser().parseFromString(text, 'text/html')
    return doc.querySelector('.product-recommendations')
  }

  // Filter cards in place — no slicing, so callers can dedup across groups before
  // capping. Ownership dedup (in-cart / already-purchased, consumable-tag-aware)
  // always applies. The same-product-type guard and fitment filter are heuristics
  // for algorithmic recs only: merchant-curated complements are a deliberate manual
  // assignment (a catch can's recirculation kit, bottom tank, and filter top share
  // the can's product type and may carry no/other-machine fitment, yet are exactly
  // what must surface), so both are bypassed for the complementary intent.
  _filterCards(grid, intent) {
    const curated = intent === 'complementary'
    const excludeIds = this._excludedProductIds()
    const excludeTypes = curated ? null : this._cartProductTypes()
    Array.from(grid.children).forEach(card => {
      const id = card.getAttribute('data-product-id')
      if (id && excludeIds.has(String(id))) { card.remove(); return }
      if (!excludeTypes) return
      const type = (card.getAttribute('data-product-type') || '').trim().toLowerCase()
      if (type && excludeTypes.has(type)) card.remove()
    })
    if (!curated && this._fitment) this._fitment.applyFitFilter(grid)
  }

  _sliceCards(grid, limit) {
    Array.from(grid.children).slice(limit).forEach(card => card.remove())
  }

  // Exclude both items currently in the cart and products the customer has
  // already purchased (server-rendered into data-purchased-ids). Don't suggest
  // something they own or are about to re-see in their own cart.
  _excludedProductIds() {
    const ids = new Set(this.purchasedIds)
    const cart = this._latestCart
    if (cart && cart.items) {
      cart.items.forEach(i => ids.add(String(i.product_id)))
    }
    return ids
  }

  // Drop recommendations that share a product type with anything already in the
  // cart — don't suggest a second intake when they just added one. Relies on
  // /cart.js line items exposing product_type and the card carrying
  // data-product-type (cart-suggestion-card.liquid).
  _cartProductTypes() {
    const cart = this._latestCart
    if (!cart || !cart.items) return new Set()
    const types = new Set()
    cart.items.forEach(i => {
      const t = (i.product_type || '').trim().toLowerCase()
      if (t) types.add(t)
    })
    return types
  }

  _parseIdSet(value) {
    if (!value) return new Set()
    return new Set(value.split(',').map(s => s.trim()).filter(Boolean))
  }

  _stayHidden() {
    this.setAttribute('hidden', '')
    this.removeAttribute('aria-busy')
    this._setHeadingSkeleton(false)
    if (this._slot) this._slot.replaceChildren()
  }

  // Loading affordance while recommendations fetch. Mirrors the homepage-
  // recommendations skeleton pattern; the placeholder cards reuse the real
  // .cart-suggestion-card box dimensions so the live cards replace them with no
  // layout shift. Replaced by _commitGrid (real cards) or cleared by _stayHidden.
  _showSkeleton() {
    if (!this._slot) return
    const count = Math.min(Math.max(this.limit, 1), 4)
    const frag = document.createDocumentFragment()
    for (let i = 0; i < count; i++) frag.appendChild(this._skeletonCard())
    this._slot.replaceChildren(frag)
    this._setHeadingSkeleton(true)
    this.setAttribute('aria-busy', 'true')
    this.removeAttribute('hidden')
  }

  _skeletonCard() {
    const card = document.createElement('div')
    card.className = 'cart-suggestion-card cart-suggestion-card--skeleton'
    card.setAttribute('aria-hidden', 'true')
    card.innerHTML =
      '<div class="cart-suggestion-card__image cart-suggestion-skeleton__box"></div>'
      + '<div class="cart-suggestion-card__details">'
      + '<div class="cart-suggestion-skeleton__line cart-suggestion-skeleton__line--title"></div>'
      + '<div class="cart-suggestion-skeleton__line cart-suggestion-skeleton__line--price"></div>'
      + '<div class="cart-suggestion-skeleton__btn"></div>'
      + '</div>'
    return card
  }

  _teardown() {
    if (this._fetchAbort) this._fetchAbort.abort()
    this._fetchToken += 1
    this._stayHidden()
  }
}

customElements.define('cart-drawer-suggestions', CartDrawerSuggestions)
